/**
 * Taxes: help with the year's tax return, one country at a time.
 *
 * A section of its own rather than a corner of the journal: a tax return is
 * about more than trades -- PIT-38 takes share sales, crypto and dividends too --
 * and the next country added should not have to fit inside a trading screen.
 *
 * Laid out from the "Trading Dziennik v2" design: the country cards, the year
 * with the journal switch, the estimate and its deadline on top, then what is
 * typed in on the left and what to copy into the form on the right.
 *
 * Every country is one entry in `TAX_COUNTRIES`: a key and a `model` that turns
 * the journal and what was typed into the parts the page draws (hero, groups,
 * form lines, notes). The arithmetic itself lives in shared/compute.js
 * (`pit38`, `form6781`, `usTax`, `deTax`, `ukTax`), where it is tested on its
 * own. What is typed stays on this device, per year. None of it is tax advice,
 * and every country says so.
 */

import { h } from '../vdom.js';
import { C, MONO, css, card, input, button, tap, removeButton, taxesAllowed, planLocked } from './common.js';
import { pit38, form6781, form8949, FORM_8949_BOXES, usTax, deTax, ukTax, US_BRACKETS, UK_AEA, tradesFingerprint } from '../../../shared/compute.js';
import { parseNumber, resolveRates, toBase, TAX_VOTE_KEYS, isTaxableTrade } from '../../../shared/model.js';

/* The countries, in the order the cards show them. Adding one: an entry here, `t.tax.<key>`, and its arithmetic in compute. */
export const TAX_COUNTRIES = [
  { key: 'pl', code: 'PL', ready: true, model: ctx => plModel(ctx) },
  { key: 'us', code: 'US', model: ctx => usModel(ctx) },
  /* Shares and the rest of Schedule D: the first country asked for in the Taxes tab. */
  { key: 'us8949', code: 'US', model: ctx => us8949Model(ctx) },
  { key: 'de', code: 'DE', model: ctx => deModel(ctx) },
  { key: 'uk', code: 'UK', model: ctx => ukModel(ctx) }
];

const countryOf = key => TAX_COUNTRIES.find(country => country.key === key) || TAX_COUNTRIES[0];
const GOLD = '#E3B86B';

// ------------------------------------------------------------- the period ----

/* The UK's tax year runs 6 April to 5 April and is named by both years. */
const ukLabel = year => year + '/' + String((year + 1) % 100).padStart(2, '0');

function periodOf(country, year) {
  if (country === 'uk') return { from: year + '-04-06', to: (year + 1) + '-04-05', label: ukLabel(year) };
  return { from: year + '-01-01', to: year + '-12-31', label: String(year) };
}

/* The year's trades taken with one's own money -- never backtests, never prop accounts (see isTaxableTrade) -- inside the country's tax year. */
const tradesIn = (state, period) => (state.trades || [])
  .filter(row => isTaxableTrade(row) && row.date >= period.from && row.date <= period.to);

/* Prop-account trades from the same period: left out, and said so. */
const propTradesIn = (state, period) => (state.trades || [])
  .filter(row => !row.backtestId && row.propAccountId && row.date >= period.from && row.date <= period.to);

/*
 * A trade's result in another currency, through the rates kept in Preferences
 * for its month (via the base currency). Null when a rate is missing: such a
 * trade is left out and counted, never guessed.
 */
function convert(row, to, state) {
  const amount = Number(row.netPl) || 0;
  const from = String(row.currency || 'USD').toUpperCase();
  if (from === to) return amount;
  const base = state.settings?.baseCurrency || 'PLN';
  const rates = resolveRates(state.data?.rateHistory || [], String(row.date).slice(0, 7));
  const inBase = toBase(amount, from, base, rates);
  if (inBase === null) return null;
  if (to === base) return inBase;
  const rate = rates[to];
  return Number.isFinite(rate) && rate > 0 ? inBase / rate : null;
}

/* The journal's side of a basket: gains, losses and the trades that had no rate. */
function journalIn(state, period, currency, include) {
  const all = tradesIn(state, period);
  const values = include ? all.map(row => ({ row, value: convert(row, currency, state) })) : [];
  const counted = values.filter(item => item.value !== null);
  return {
    all,
    counted,
    gains: counted.filter(item => item.value > 0).reduce((sum, item) => sum + item.value, 0),
    losses: -counted.filter(item => item.value < 0).reduce((sum, item) => sum + item.value, 0),
    missing: values.length - counted.length
  };
}

// --------------------------------------------------------------- the page ----

export function renderTaxes(ctx) {
  const { state, t, actions, mobile = false } = ctx;
  if (!taxesAllowed(state)) {
    return planLocked({ t, actions, title: t.shell.planTaxesTitle, text: t.shell.planTaxesLocked, compact: mobile });
  }
  /* Every country starts from the journal, fetched once for the whole app. */
  if (state.trades === null && !state.tradesFailed) actions.loadTrades();
  actions.loadTaxVotes();

  const x = t.tax;
  const country = countryOf(state.taxCountry || (state.lang === 'PL' ? 'pl' : 'us'));
  const year = state.pitYear;
  const period = periodOf(country.key, year);
  const wide = !mobile && (typeof window === 'undefined' || window.innerWidth >= 1180);
  const model = country.model({ ...ctx, x, year, period });
  const include = model.include;
  /*
   * A trade left out for want of a rate: the summary's own currency, or the
   * trade's, has none at all yet -- fetch it, as a new entry's currency is.
   * (Poland converts at NBP rates of its own.)
   */
  if (model.missing > 0 && country.key !== 'pl' && actions.ensureRates) {
    const known = new Set((state.data?.rateHistory || []).map(row => String(row.currency).toUpperCase()));
    const own = { us: 'USD', us8949: 'USD', de: 'EUR', uk: 'GBP' }[country.key];
    const codes = [own, ...tradesIn(state, period).map(row => String(row.currency || 'USD').toUpperCase())];
    actions.ensureRates(codes.filter(code => !known.has(code)));
  }
  const thisYear = new Date().getFullYear();

  const pill = (label, on, go, key) => h('div', {
    key, ...tap(go, label), class: 'pt-button', 'aria-pressed': String(on),
    style: css({
      flex: '0 0 auto', height: '36px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '9px',
      border: `1px solid ${on ? C.accent : C.stroke}`, background: on ? C.accentDeep : 'transparent',
      fontFamily: MONO, fontSize: '12px', color: on ? C.text : C.textDim, cursor: 'pointer', whiteSpace: 'nowrap'
    })
  }, label);

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: mobile ? '12px' : '16px' }) },
    /* Whose return. */
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '6px 12px', flexWrap: 'wrap' }) },
        h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }) }, x.head),
        h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, x.headSub)),
      h('div', {
        style: css({ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(170px, 1fr))', gap: '8px' })
      }, ...TAX_COUNTRIES.map(option => countryCard(option, option.key === country.key, x, () => actions.setTaxCountry(option.key), mobile)))
    ),

    /* Which year, and whether the journal counts. */
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px' }) },
      h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }) }, x.year),
      h('div', { style: css({ display: 'flex', gap: '6px', overflowX: 'auto', maxWidth: '100%' }) },
        ...[thisYear, thisYear - 1, thisYear - 2, thisYear - 3].map(option =>
          pill(country.key === 'uk' ? ukLabel(option) : String(option), option === year, () => actions.setPitYear(option), 'y' + option))),
      h('div', { style: css({ flex: 1 }) }),
      journalSwitch(include, x.journal, x.journalCount(tradesIn(state, period).length, period.label), model.toggleJournal),
      model.extraControl || null
    ),
    propTradesIn(state, period).length > 0 && h('div', {
      key: 'prop-out', style: css({ fontSize: '12px', lineHeight: 1.5, color: C.muted, maxWidth: '760px' })
    }, x.propExcluded(propTradesIn(state, period).length)),

    /*
     * Keyed by country and year. The figure boxes are seeded once and then left
     * alone, so without the key a box kept showing 2025's figure after
     * switching to 2024 -- and whatever was typed next went into 2024.
     */
    h('div', {
      key: country.key + '-' + year, style: css({ display: 'flex', flexDirection: 'column', gap: mobile ? '12px' : '16px' })
    },
      heroCard(model.hero, mobile),

      model.missing > 0 && h('div', { key: 'missing', style: css({ fontSize: '12px', color: C.danger, lineHeight: 1.5 }) }, x.unconverted(model.missing)),

      h('div', {
        key: 'grid',
        style: css({ display: 'grid', gridTemplateColumns: wide ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)', gap: '14px', alignItems: 'start' })
      },
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }) }, ...model.left),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }) }, ...model.right)
      ),
      h('div', { key: 'disc', style: css({ fontSize: '12px', lineHeight: 1.5, color: C.muted }) }, model.disclaimer)
    ),

    laterCard({ state, x, actions })
  );
}

// ------------------------------------------------------------ the pieces ----

function countryCard(option, on, x, go, mobile = false) {
  const [name, form] = x.countries[option.key];
  return h('div', {
    key: option.key, ...tap(go, name), class: 'pt-button', 'aria-pressed': String(on),
    style: css({
      minHeight: '64px', minWidth: 0, padding: '10px 14px', borderRadius: '12px', cursor: 'pointer',
      border: `1px solid ${on ? C.accent : C.stroke}`, background: on ? C.line : C.card,
      display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }) },
      h('span', { style: css({ fontFamily: MONO, fontSize: '11px', fontWeight: 700, letterSpacing: '.6px', color: C.muted }) }, option.code || option.key.toUpperCase()),
      /* On a phone the name gets the room: two lines rather than an ellipsis, and no badge beside it. */
      h('span', { style: css({ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 700, ...(mobile ? { lineHeight: 1.25 } : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }) }, name),
      !mobile && h('span', {
        key: 'badge',
        style: css({
          flex: '0 0 auto', padding: '2px 7px', borderRadius: '5px', background: C.accentDeep,
          fontFamily: MONO, fontSize: '9.5px', fontWeight: 700, letterSpacing: '.6px', color: C.accentHi
        })
      }, option.ready ? x.ready : x.fresh)),
    h('span', { style: css({ fontFamily: MONO, fontSize: '11px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, form)
  );
}

function journalSwitch(on, label, count, go) {
  return h('div', {
    ...tap(go, label), class: 'pt-button', role: 'switch', 'aria-checked': String(on),
    style: css({
      minHeight: '44px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px 0 10px', borderRadius: '10px', cursor: 'pointer',
      border: `1px solid ${on ? C.accentDeep : C.stroke}`, background: on ? C.line : 'transparent'
    })
  },
    h('span', {
      style: css({ width: '34px', height: '20px', borderRadius: '10px', background: on ? C.accent : C.strokeHi, position: 'relative', flex: '0 0 34px' })
    }, h('span', {
      style: css({ position: 'absolute', top: '2px', left: on ? '16px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: C.text })
    })),
    h('span', { style: css({ fontSize: '13px' }) }, label),
    h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.muted }) }, count)
  );
}

function heroCard(hero, mobile) {
  return card({
    padding: mobile ? '16px' : '20px 22px', borderRadius: '16px', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '16px 32px'
  },
    h('div', { style: css({ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
      h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }) }, hero.cap),
      h('span', {
        style: css({ fontFamily: MONO, fontSize: mobile ? '28px' : '36px', fontWeight: 700, letterSpacing: '-1px', color: hero.color, whiteSpace: 'nowrap' })
      }, hero.val),
      h('span', { style: css({ fontSize: '12.5px', color: C.textDim }) }, hero.sub)),
    h('div', {
      style: css({ flex: '2 1 360px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' })
    }, ...hero.parts.map((part, i) => h('div', {
      key: 'p' + i,
      style: css({
        padding: '10px 12px', borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}`,
        display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0
      })
    },
      h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, part.label),
      h('span', { style: css({ fontFamily: MONO, fontSize: '15px', fontWeight: 700, color: part.color || C.text }) }, part.val),
      h('span', { style: css({ fontSize: '11px', color: C.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, part.sub)
    )))
  );
}

/* A panel with a title, a one-line explanation and, on the right, its result. */
function panel({ title, sub, res, resColor, resLab, key }, ...children) {
  return card({ key, padding: '16px 18px 18px', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '12px' },
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '12px' }) },
      h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }) },
        h('span', { style: css({ fontSize: '15px', fontWeight: 700 }) }, title),
        sub && h('span', { key: 's', style: css({ fontSize: '12.5px', color: C.muted }) }, sub)),
      res !== undefined && h('div', { key: 'r', style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
        h('span', { style: css({ fontFamily: MONO, fontSize: '15px', fontWeight: 700, color: resColor || C.text, whiteSpace: 'nowrap' }) }, res),
        resLab && h('span', { key: 'l', style: css({ fontSize: '11px', color: C.muted, whiteSpace: 'nowrap' }) }, resLab))),
    ...children.filter(Boolean)
  );
}

function journalNote(label, text) {
  return h('div', {
    key: 'j',
    style: css({
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', padding: '10px 12px', borderRadius: '10px',
      background: C.line, border: `1px solid ${C.accentDeep}`
    })
  },
    h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.8px', color: C.accentHi }) }, label),
    h('span', { style: css({ flex: '1 1 200px', fontSize: '12.5px', color: C.textDim }) }, text));
}

const note = (text, color = C.muted, key = 'n') => h('span', { key, style: css({ fontSize: '12px', lineHeight: 1.5, color }) }, text);

/* Boxes for figures, two or three across, wrapping on a phone. */
function fields(...boxes) {
  return h('div', {
    key: 'f', style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '10px' })
  }, ...boxes);
}

function fieldBox({ inputs, name, label, value, unit, hint, onInput }) {
  return h('label', { key: name, style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
    h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, label),
    h('div', {
      style: css({
        display: 'flex', alignItems: 'center', gap: '6px', height: '44px', borderRadius: '10px', padding: '0 12px',
        border: `1px solid ${value ? C.strokeHi : C.stroke}`, background: C.panel
      })
    },
      input(inputs, name, '0', {
        type: 'number', value: value || '', ariaLabel: label, onInput,
        style: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: 0, fontFamily: MONO, fontSize: '14px' }
      }),
      unit && h('span', { key: 'u', style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.faint }) }, unit)),
    hint && h('span', { key: 'h', style: css({ fontSize: '11px', color: C.faint }) }, hint));
}

/* A small row of choices, one of them on. */
function segmented(options, current, go) {
  return h('div', { style: css({ display: 'flex', gap: '3px', padding: '3px', borderRadius: '9px', background: C.panel, flexWrap: 'wrap' }) },
    ...options.map(([value, label, title]) => h('div', {
      key: String(value), ...tap(() => go(value), label), class: 'pt-button', 'aria-pressed': String(value === current), title: title || null,
      style: css({
        minHeight: '36px', minWidth: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px', borderRadius: '7px',
        background: value === current ? C.strokeHi : 'transparent', color: value === current ? C.text : C.muted,
        fontFamily: MONO, fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'
      })
    }, label)));
}

function optionRow(label, sub, control, key) {
  return h('div', {
    key,
    style: css({
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', padding: '10px 12px', borderRadius: '10px',
      border: `1px solid ${C.stroke}`, background: C.panel
    })
  },
    h('div', { style: css({ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
      h('span', { style: css({ fontSize: '13px', color: C.textDim }) }, label),
      h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, sub)),
    control);
}

/* What to copy into the form, line by line; the last line can be the total. */
function formCard(title, sub, lines) {
  return panel({ title, sub, key: 'form' },
    h('div', { key: 'l', style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.strokeHi}` }) },
      ...lines.filter(Boolean).map((line, i) => h('div', {
        key: 'l' + i,
        style: css({
          display: 'grid', gridTemplateColumns: '58px minmax(0, 1fr) auto', gap: '4px 12px', alignItems: 'center', minHeight: '42px',
          padding: '6px 4px', borderBottom: `1px solid ${line.total ? 'transparent' : C.line}`, background: line.total ? C.line : 'transparent'
        })
      },
        h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', color: C.faint, whiteSpace: 'nowrap' }) }, line.no || ''),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
          h('span', { style: css({ fontSize: '13px', fontWeight: line.strong || line.total ? 700 : 400, color: line.total ? C.text : C.textDim }) }, line.label),
          line.sub && h('span', { key: 's', style: css({ fontSize: '11.5px', color: C.muted }) }, line.sub)),
        h('span', {
          style: css({
            fontFamily: MONO, fontSize: line.total ? '15px' : '13px', fontWeight: line.strong || line.total ? 700 : 400,
            color: line.color || C.text, whiteSpace: 'nowrap', textAlign: 'right'
          })
        }, line.val)
      )))
  );
}

function notesCard(notes) {
  return card({ key: 'notes', padding: '14px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
    ...notes.filter(Boolean).map((item, i) => h('div', {
      key: 'n' + i, style: css({ display: 'flex', gap: '10px', fontSize: '12.5px', lineHeight: 1.5, color: C.textDim })
    },
      h('span', { style: css({ color: item.color, fontWeight: 700 }) }, item.mark),
      h('span', null, item.text))));
}

const ok = text => ({ mark: '✓', color: C.accent, text });
const warn = text => ({ mark: '!', color: GOLD, text });
const aside = text => ({ mark: '·', color: C.faint, text });

/* A sign-coloured figure: green for a gain, red for a loss, plain for nothing. */
const tone = value => (value > 0.5 ? C.accent : value < -0.5 ? C.danger : C.text);

/* Whole units of a currency, the way a tax form wants them. */
const wholeIn = (fmt, code) => value => fmt.money(Math.round(value), 0, code);
const signedIn = (fmt, code) => value => (value > 0.5 ? '+' : value < -0.5 ? '−' : '') + fmt.money(Math.abs(Math.round(value)), 0, code);

/* "I need this country": what to build next, asked for by the people who would use it. */
function laterCard({ state, x, actions }) {
  const mine = state.taxVotes || [];
  const canVote = state.user && !state.user.isGuest;
  const items = [...TAX_VOTE_KEYS, 'nl'];
  return card({ padding: '14px 16px', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '10px' },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
      h('span', { style: css({ fontSize: '14px', fontWeight: 700 }) }, x.later.title),
      h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, canVote ? x.later.sub : x.later.subDemo)),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '8px' }) },
      ...items.map(key => {
        const skip = !TAX_VOTE_KEYS.includes(key);
        /* A demo cannot ask: its account is gone in three days, so the request would count for nothing. */
        const votable = !skip && canVote;
        const on = mine.includes(key);
        const [name, why] = x.later.items[key];
        const go = () => { if (!skip && canVote) actions.toggleTaxVote(key); };
        return h('div', {
          key, ...(votable ? tap(go, name) : {}), class: votable ? 'pt-button' : null, title: skip ? x.later.skipTitle : null,
          'aria-pressed': votable ? String(on) : null,
          style: css({
            minHeight: '44px', display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 12px', borderRadius: '10px',
            border: `1px solid ${on ? C.accentDeep : skip ? C.line : C.stroke}`, background: on ? C.line : 'transparent',
            cursor: votable ? 'pointer' : 'default'
          })
        },
          h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px' }) },
            h('span', { style: css({ fontSize: '13px', fontWeight: 600, color: skip ? C.muted : C.text }) }, name),
            h('span', { style: css({ fontSize: '11px', color: C.muted }) }, why)),
          (skip || canVote) && h('span', {
            key: 'mark', style: css({ fontFamily: MONO, fontSize: '11px', fontWeight: 700, color: skip ? C.faint : on ? C.accentHi : C.accent })
          }, skip ? x.later.skip : on ? x.later.asked : x.later.need));
      }))
  );
}

// ------------------------------------------------------------ the inputs ----

/* What was typed for a country other than Poland, for the year on screen. */
const savedFor = (state, country) => (state.taxInputs[country] || {})[String(state.pitYear)] || {};

/* A figure typed in, as a number; nothing typed is 0. */
const figure = (saved, key) => parseNumber(saved[key]) || 0;

// ------------------------------------------------------- Poland · PIT-38 ----

function plModel({ state, t, fmt, inputs, actions, x, year, period }) {
  const s = x.pl;
  const saved = state.pitInputs[String(year)] || {};
  const value = key => figure(saved, key);
  const include = saved.journal !== 'no';
  /* NBP figures count only while the journal's trades are the ones they were made from. */
  const converted = state.pitConverted && state.pitConverted.year === String(year)
    && state.pitConverted.fingerprint === tradesFingerprint(tradesIn(state, period))
    ? state.pitConverted : null;
  const zl = wholeIn(fmt, 'PLN');
  const zlSigned = signedIn(fmt, 'PLN');
  const lossYears = [5, 4, 3, 2, 1].map(back => year - back);

  /* Exact once converted at NBP rates; until then the rates in Preferences, and the page says so. */
  const journal = journalIn(state, period, 'PLN', include);
  const trades = include
    ? (converted ? converted.items.map(item => ({ pln: item.pln })) : journal.counted.map(item => ({ pln: item.value })))
    : [];
  const tradeGains = trades.filter(row => row.pln > 0).reduce((sum, row) => sum + row.pln, 0);
  const tradeCosts = -trades.filter(row => row.pln < 0).reduce((sum, row) => sum + row.pln, 0);

  const result = pit38({
    trades,
    pit8c: { income: value('p8Income'), cost: value('p8Cost') },
    securities: { income: value('secIncome'), cost: value('secCost') },
    losses: lossYears.map(lossYear => ({
      year: lossYear, amount: value('loss' + lossYear), used: value('used' + lossYear), once: saved['once' + lossYear] === 'yes'
    })),
    crypto: { income: value('cryptoIncome'), cost: value('cryptoCost'), carried: value('cryptoCarried') },
    dividends: { gross: value('divGross'), withheld: value('divWithheld') }
  });
  const cap = result.capital;
  const crypto = result.crypto;
  const div = result.dividends;
  const cryptoResult = crypto.income - crypto.cost - crypto.carried;

  const box = (key, label) => fieldBox({
    inputs, name: 'pit_' + key, label, value: saved[key], unit: 'zł', onInput: event => actions.setPitInput(key, event.target.value)
  });

  const jText = !include ? '' : journal.all.length
    ? s.jText(journal.all.length, zl(tradeGains), zl(tradeCosts), Boolean(converted))
    : x.noTrades;

  /* Field numbers are the 2025 form's (PIT-38(18)); another year gets the parts only. */
  const numbered = year === 2025;
  const no = (field, part) => (numbered ? s.field(field) : s.part(part));

  const losses = lossYears.map(lossYear => {
    const deduction = cap.deductions.find(row => row.year === lossYear);
    const available = deduction ? deduction.available : 0;
    const deducted = deduction ? deduction.deducted : 0;
    const remaining = available - deducted;
    const last = lossYear + 5 === year;
    const mode = saved['once' + lossYear] === 'yes' ? 'once' : 'half';
    const lossBox = (key, label) => h('div', { key, style: css({ flex: '1 1 110px', minWidth: 0 }) },
      fieldBox({ inputs, name: 'pit_' + key + lossYear, label, value: saved[key + lossYear], unit: 'zł',
        onInput: event => actions.setPitInput(key + lossYear, event.target.value) }));
    return h('div', {
      key: 'loss' + lossYear,
      style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px 12px', padding: '10px 2px', borderBottom: `1px solid ${C.line}` })
    },
      h('div', { style: css({ flex: '0 0 64px', display: 'flex', flexDirection: 'column', gap: '2px', paddingBottom: '10px' }) },
        h('span', { style: css({ fontFamily: MONO, fontSize: '14px', fontWeight: 700 }) }, String(lossYear)),
        h('span', { style: css({ fontSize: '10.5px', color: last && available > 0 ? GOLD : C.faint, whiteSpace: 'nowrap' }) },
          last ? s.lossLast : s.lossUntil(lossYear + 5))),
      lossBox('loss', s.lossAmount),
      lossBox('used', s.lossUsed),
      available > 0 && h('div', {
        key: 'mode', style: css({ flex: '1 1 100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', paddingLeft: '76px' })
      },
        segmented([['half', s.lossHalf, s.lossHalfTitle], ['once', s.lossOnce, s.lossOnceTitle]], mode,
          next => actions.setPitInput('once' + lossYear, next === 'once' ? 'yes' : 'no')),
        h('span', { style: css({ flex: '1 1 160px', fontFamily: MONO, fontSize: '12px', color: C.textDim }) },
          deducted ? s.lossDeduct(zl(deducted)) : s.lossNoIncome),
        h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) },
          remaining > 0.5 ? (last ? s.lossLapses(zl(remaining)) : s.lossLeft(zl(remaining))) : s.lossDone))
    );
  });

  return {
    include,
    toggleJournal: () => actions.setPitInput('journal', include ? 'no' : 'yes'),
    missing: converted ? 0 : journal.missing,
    extraControl: include && h('div', { key: 'nbp' },
      button(state.pitLoading ? x.nbpBusy : converted ? x.nbpDone : x.nbp, actions.convertPitTrades, {
        tone: converted ? 'quiet' : 'accent',
        style: {
          height: '44px', display: 'flex', alignItems: 'center', padding: '0 16px', borderRadius: '10px',
          opacity: state.pitLoading || !journal.all.length ? 0.5 : 1,
          ...(converted ? { background: 'transparent', border: `1px solid ${C.accentDeep}`, color: C.accentHi } : {})
        }
      })),
    hero: {
      cap: s.heroCap(year + 1),
      val: zl(result.total),
      color: result.total ? C.text : C.accent,
      sub: result.total ? s.heroDue(year + 1) : s.heroNone,
      parts: [
        { label: s.partSec, val: zl(cap.tax), sub: s.partSecSub(zl(cap.base)), color: cap.tax ? C.text : C.textDim },
        { label: s.partCrypto, val: zl(crypto.tax), sub: cryptoResult < 0 ? s.partCryptoCarry(zl(-cryptoResult)) : s.partCryptoGain(zl(Math.max(0, cryptoResult))), color: crypto.tax ? C.text : C.textDim },
        { label: s.partDiv, val: zl(div.tax), sub: s.partDivSub(zl(div.credit)), color: div.tax ? C.text : C.textDim }
      ]
    },
    left: [
      panel({
        key: 'sec', title: s.secTitle, sub: s.secSub, res: zlSigned(cap.result), resColor: tone(cap.result),
        resLab: cap.result < 0 ? s.secLoss(year + 1, year + 5) : x.income
      },
        include && journalNote(x.fromJournal, jText),
        fields(box('p8Income', s.p8Income), box('p8Cost', s.p8Cost), box('secIncome', s.secIncome), box('secCost', s.secCost)),
        note(s.secNote)),
      panel({
        key: 'crypto', title: s.cryptoTitle, sub: s.cryptoSub, res: zlSigned(cryptoResult), resColor: tone(cryptoResult),
        resLab: cryptoResult < 0 ? s.cryptoCarryLab(year + 1) : x.income
      },
        fields(box('cryptoIncome', s.cryptoIncome), box('cryptoCost', s.cryptoCost(year)), box('cryptoCarried', s.cryptoCarried)),
        cryptoResult < 0 && note(s.cryptoCarryNote(zl(-cryptoResult), year + 1), GOLD)),
      panel({ key: 'div', title: s.divTitle, sub: s.divSub, res: zl(div.tax), resColor: div.tax ? C.text : C.textDim, resLab: s.divLab },
        fields(box('divGross', s.divGross), box('divWithheld', s.divWithheld)),
        div.gross > 0 && note(value('divWithheld') > div.due + 0.005 ? s.divOver(zl(div.due)) : s.divUs,
          value('divWithheld') > div.due + 0.005 ? GOLD : C.muted))
    ],
    right: [
      panel({
        key: 'losses', title: s.lossTitle, sub: s.lossSub, res: zl(cap.deducted), resColor: cap.deducted ? C.accent : C.textDim, resLab: s.lossYear(year)
      },
        h('div', { key: 'rows', style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.strokeHi}` }) }, ...losses),
        note(s.lossNote)),
      formCard(s.formTitle, numbered ? s.formSub2025 : s.formSubOther, [
        { no: no(20, 'C'), label: s.lines.p8Income, val: zl(cap.pit8c.income) },
        { no: no(21, 'C'), label: s.lines.p8Cost, val: zl(cap.pit8c.cost) },
        { no: no(22, 'C'), label: s.lines.otherIncome, sub: s.lines.otherIncomeSub, val: zl(cap.other.income) },
        { no: no(23, 'C'), label: s.lines.otherCost, val: zl(cap.other.cost) },
        { no: no(26, 'C'), label: s.lines.income, val: zl(cap.income) },
        { no: no(27, 'C'), label: s.lines.cost, val: zl(cap.cost) },
        cap.result >= 0
          ? { no: no(28, 'C'), label: s.lines.gain, val: zl(cap.result), color: tone(cap.result) }
          : { no: no(29, 'C'), label: s.lines.loss, val: zl(-cap.result), color: C.danger },
        { no: no(30, 'D'), label: s.lines.losses, val: zl(cap.deducted), color: cap.deducted ? C.accent : C.text },
        { no: no(31, 'D'), label: s.lines.base, val: zl(cap.base) },
        { no: no(33, 'D'), label: s.lines.tax, val: zl(cap.tax) },
        { no: no(35, 'D'), label: s.lines.due, val: zl(cap.tax), strong: true },
        (crypto.income || crypto.cost || crypto.carried) && { no: no(36, 'E'), label: s.lines.cIncome, val: zl(crypto.income) },
        (crypto.income || crypto.cost || crypto.carried) && { no: numbered ? s.field('37–38') : s.part('E'), label: s.lines.cCost, sub: s.lines.cCostSub, val: zl(crypto.cost + crypto.carried) },
        (crypto.income || crypto.cost || crypto.carried) && (cryptoResult >= 0
          ? { no: no(39, 'E'), label: s.lines.cGain, val: zl(cryptoResult) }
          : { no: no(40, 'E'), label: s.lines.cCarry, val: zl(-cryptoResult), color: GOLD }),
        (crypto.income || crypto.cost || crypto.carried) && { no: no(45, 'F'), label: s.lines.cTax, val: zl(crypto.tax), strong: true },
        div.gross > 0 && { no: no(47, 'G'), label: s.lines.dTax, val: zl(div.due) },
        div.gross > 0 && { no: no(48, 'G'), label: s.lines.dCredit, val: zl(div.credit) },
        div.gross > 0 && { no: no(49, 'G'), label: s.lines.dDue, val: zl(div.tax), strong: true },
        { no: numbered ? s.field(51) : '', label: s.lines.total, val: zl(result.total), total: true, color: result.total ? C.text : C.accent }
      ])
    ],
    disclaimer: s.disclaimer
  };
}

// ------------------------------------------------- USA · Form 6781 §1256 ----

function usModel({ state, fmt, inputs, actions, x, year, period }) {
  const s = x.us;
  const saved = savedFor(state, 'us');
  const include = saved.journal !== 'no';
  const bracket = US_BRACKETS.includes(Number(saved.bracket)) ? Number(saved.bracket) : 24;
  const usd = wholeIn(fmt, 'USD');
  const usdSigned = signedIn(fmt, 'USD');

  const journal = journalIn(state, period, 'USD', include);
  const f = form6781({
    trades: journal.counted.map(item => ({ pair: item.row.pair, usd: item.value })),
    picks: saved.picks || {},
    other: figure(saved, 'other'),
    mtm: figure(saved, 'mtm')
  });
  const stateRate = figure(saved, 'state');
  const tax = usTax({ net: f.net, bracket, stateRate });
  const blended = fmt.number(Math.round(tax.blended * 10) / 10, 1).replace(/[.,]0$/, '') + '%';

  const box = (key, label, unit, hint) => fieldBox({
    inputs, name: 'us_' + key, label, value: saved[key], unit, hint, onInput: event => actions.setTaxInput('us', key, event.target.value)
  });
  const picked = f.instruments.filter(row => row.picked);

  const chip = (row) => h('div', {
    key: 'p' + row.root, ...tap(() => actions.setTaxInput('us', 'pick:' + row.root, !row.picked), row.root), class: 'pt-button',
    'aria-pressed': String(row.picked),
    style: css({
      padding: '6px 10px', borderRadius: '999px', cursor: 'pointer', fontFamily: MONO, fontSize: '11px', whiteSpace: 'nowrap', textAlign: 'center',
      border: `1px solid ${row.picked ? C.accent : C.stroke}`, color: row.picked ? C.accent : C.muted
    })
  }, row.picked ? s.yes : s.no);

  return {
    include,
    toggleJournal: () => actions.setTaxInput('us', 'journal', include ? 'no' : 'yes'),
    missing: journal.missing,
    hero: {
      cap: s.heroCap(year, year + 1),
      val: usd(tax.tax),
      color: tax.tax ? C.text : C.accent,
      sub: f.net < 0 ? s.heroLoss(usdSigned(f.net)) : s.heroSub(usdSigned(f.net), blended, stateRate ? fmt.number(stateRate) : ''),
      parts: [
        { label: s.partLt, val: usd(tax.longTermTax), sub: usd(tax.longTerm) + ' × ' + tax.ltRate + '%', color: C.accentHi },
        { label: s.partSt, val: usd(tax.shortTermTax), sub: usd(tax.shortTerm) + ' × ' + bracket + '%', color: GOLD },
        { label: s.partAll, val: usd(tax.allShort), sub: s.partSave(usd(Math.max(0, tax.save))), color: C.textDim }
      ]
    },
    left: [
      panel({ key: 'rate', title: s.rateTitle, sub: s.rateSub },
        h('div', {
          key: 'br',
          style: css({
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))', gap: '4px', padding: '3px',
            borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}`
          })
        }, ...US_BRACKETS.map(rate => h('div', {
          key: 'b' + rate, ...tap(() => actions.setTaxInput('us', 'bracket', rate), rate + '%'), class: 'pt-button', 'aria-pressed': String(rate === bracket),
          style: css({
            height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', cursor: 'pointer',
            background: rate === bracket ? C.strokeHi : 'transparent', boxShadow: rate === bracket ? `inset 0 0 0 1px ${C.accent}` : 'none',
            fontFamily: MONO, fontSize: '13px', fontWeight: 700, color: rate === bracket ? C.text : C.muted
          })
        }, rate + '%'))),
        fields(box('other', s.other, '$', s.otherHint), box('mtm', s.mtm, '$', s.mtmHint), box('state', s.state, '%', s.stateHint))),
      panel({ key: 'split', title: s.splitTitle, sub: s.splitSub },
        /* The labels wrap rather than clip: on a phone the 40% side is about 130px wide. */
        h('div', { key: 'bar', style: css({ display: 'flex', minHeight: '34px', borderRadius: '9px', overflow: 'hidden', gap: '2px' }) },
          h('div', {
            style: css({ flex: 60, display: 'flex', alignItems: 'center', padding: '6px 12px', background: C.accentDeep, fontFamily: MONO, fontSize: '12px', fontWeight: 700, color: C.accentHi, lineHeight: 1.3, minWidth: 0, overflowWrap: 'anywhere' })
          }, '60% · ' + s.lt(tax.ltRate)),
          h('div', {
            style: css({ flex: 40, display: 'flex', alignItems: 'center', padding: '6px 12px', background: C.goldBg, fontFamily: MONO, fontSize: '12px', fontWeight: 700, color: GOLD, lineHeight: 1.3, minWidth: 0, overflowWrap: 'anywhere' })
          }, '40% · ' + s.st(bracket))),
        h('div', { key: 'tiles', style: css({ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }) },
          ...[[s.blended, blended, s.blendedSub(bracket), C.accentHi], [s.save, f.net > 0 ? usd(tax.save) : usd(0), s.saveSub, tax.save > 0 ? C.accent : C.textDim]]
            .map(([cap, val, sub, color], i) => h('div', {
              key: 't' + i,
              style: css({ padding: '12px 14px', borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}`, display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 })
            },
              h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }) }, cap),
              h('span', { style: css({ fontFamily: MONO, fontSize: '19px', fontWeight: 700, color }) }, val),
              h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, sub))))),
      panel({ key: 'inst', title: s.instTitle, sub: s.instSub },
        state.trades === null ? note(x.noTrades)
          : !f.instruments.length ? note(include ? s.noTrades(year) : x.journalOff)
            : h('div', { key: 'list', style: css({ display: 'flex', flexDirection: 'column' }) },
              ...f.instruments.map((row, i) => h('div', {
                key: row.root,
                style: css({
                  display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr) auto', alignItems: 'center', gap: '10px', padding: '7px 0',
                  borderTop: i ? `1px solid ${C.line}` : 'none'
                })
              },
                chip(row),
                h('span', { style: css({ fontFamily: MONO, fontSize: '13px', fontWeight: 700, opacity: row.picked ? 1 : 0.5 }) },
                  row.root, h('span', { style: css({ fontWeight: 400, fontSize: '11px', color: C.faint }) }, ' · ' + s.trades(row.count))),
                h('span', { style: css({ fontFamily: MONO, fontSize: '12.5px', color: row.picked ? tone(row.net) : C.faint }) }, usdSigned(row.net))))),
        note(s.pickNote))
    ],
    right: [
      formCard(s.formTitle, s.formSub(year), [
        ...picked.map(row => ({ no: s.line(1), label: s.lines.inst(row.root), sub: s.lines.instSub(row.count), val: usdSigned(row.net), color: tone(row.net) })),
        { no: s.line(1), label: s.lines.other, val: usdSigned(f.other), color: tone(f.other) },
        { no: s.line(4), label: s.lines.mtm, val: usdSigned(f.mtm), color: tone(f.mtm) },
        { no: s.line(7), label: s.lines.net, val: usdSigned(f.line7), color: tone(f.line7), strong: true },
        { no: s.line(8), label: s.lines.st, sub: s.lines.stSub, val: usdSigned(f.shortTerm) },
        { no: s.line(9), label: s.lines.lt, sub: s.lines.ltSub, val: usdSigned(f.longTerm) }
      ]),
      notesCard([
        ok(s.notes.noLots),
        ok(s.notes.which),
        f.net < 0 ? warn(s.notes.lossNow) : aside(s.notes.lossLater),
        aside(s.notes.approx)
      ])
    ],
    disclaimer: s.disclaimer
  };
}

// ------------------------------------------------ USA · Form 8949 · shares ----

/*
 * The 1099-B boxes, as the form names them: A-C short-term, D-F long-term;
 * A and D with the cost basis reported to the IRS, B and E without, C and F
 * with no 1099-B at all.
 */
const BOX_TERM = { A: 'short', B: 'short', C: 'short', D: 'long', E: 'long', F: 'long' };

function us8949Model({ state, fmt, inputs, actions, x, year, period }) {
  const s = x.us8949;
  const saved = savedFor(state, 'us8949');
  const futures = savedFor(state, 'us');
  const include = saved.journal !== 'no';
  /* The bracket and the state rate are the same person's as on the futures card, unless set here. */
  const bracketRaw = saved.bracket ?? futures.bracket;
  const bracket = US_BRACKETS.includes(Number(bracketRaw)) ? Number(bracketRaw) : 24;
  const stateRate = figure(saved.state !== undefined ? saved : futures, 'state');
  const separate = saved.separate === 'yes';
  const with6781 = saved.with6781 !== 'no';
  const allBoxes = saved.allBoxes === 'yes';
  const usd = wholeIn(fmt, 'USD');
  const usdSigned = signedIn(fmt, 'USD');

  /*
   * The journal: what is not a Section 1256 contract -- the instruments left
   * unticked on the futures card -- goes in as short-term. Those ticked are
   * Form 6781's, carried in on lines 4 and 11.
   */
  const journal = journalIn(state, period, 'USD', include);
  const f = form6781({
    trades: journal.counted.map(item => ({ pair: item.row.pair, usd: item.value })),
    picks: futures.picks || {},
    other: figure(futures, 'other'),
    mtm: figure(futures, 'mtm')
  });
  const stockRows = f.instruments.filter(row => !row.picked);
  const journalShort = include ? stockRows.reduce((sum, row) => sum + row.net, 0) : 0;
  const journalCount = stockRows.reduce((sum, row) => sum + row.count, 0);

  const rows = Array.isArray(saved.rows) ? saved.rows : [];
  const r = form8949({
    boxes: Object.fromEntries(FORM_8949_BOXES.map(key => [key, {
      proceeds: figure(saved, key + '_proceeds'), cost: figure(saved, key + '_cost'), adjustment: figure(saved, key + '_adj')
    }])),
    rows: rows.map(row => ({ ...row, proceeds: parseNumber(row.proceeds) || 0, cost: parseNumber(row.cost) || 0, adjustment: parseNumber(row.adjustment) || 0 })),
    journalShort,
    f6781: with6781 ? { shortTerm: f.shortTerm, longTerm: f.longTerm } : null,
    carryover: { short: figure(saved, 'carryShort'), long: figure(saved, 'carryLong') },
    bracket, stateRate, separate
  });

  const set = (key, value) => actions.setTaxInput('us8949', key, value);
  /* The state rate shows the futures card's until one is typed here, as the bracket does. */
  const shown = { ...saved, state: saved.state !== undefined ? saved.state : futures.state };
  const box = (key, label, hint, unit = '$') => fieldBox({
    inputs, name: 'u8_' + key, label, value: shown[key], unit, hint, onInput: event => set(key, event.target.value)
  });
  const boxPanel = key => {
    const b = r.boxes.find(row => row.key === key);
    return panel({
      key: 'box' + key, title: s.boxTitle(key), sub: s.boxSub[key],
      res: usdSigned(b.gain), resColor: tone(b.gain), resLab: BOX_TERM[key] === 'short' ? s.shortLab : s.longLab
    },
      fields(box(key + '_proceeds', s.proceeds), box(key + '_cost', s.cost), box(key + '_adj', s.adjustment, s.adjustmentHint)));
  };

  /* A sale with no 1099-B: added from the boxes below, kept with the year's inputs. */
  const addSale = () => {
    const sale = {
      description: inputs.value('u8_desc').trim(),
      acquired: inputs.value('u8_acq'),
      sold: inputs.value('u8_sold'),
      proceeds: inputs.value('u8_rowProceeds'),
      cost: inputs.value('u8_rowCost')
    };
    if (!sale.description) return actions.showError(x.us8949.descNeeded);
    if (!sale.acquired || !sale.sold || sale.sold < sale.acquired) return actions.showError(x.us8949.datesNeeded);
    if (!(parseNumber(sale.proceeds) >= 0) || !(parseNumber(sale.cost) >= 0)) return actions.showError(x.us8949.amountsNeeded);
    set('rows', [...rows, sale]);
    inputs.clear(['u8_desc', 'u8_rowProceeds', 'u8_rowCost']);
  };
  const dateBox = (name, label) => h('label', { key: name, style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
    h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, label),
    input(inputs, name, '', {
      type: 'date', ariaLabel: label,
      style: { height: '44px', boxSizing: 'border-box', width: '100%', borderRadius: '10px', colorScheme: 'dark', fontFamily: MONO }
    }));

  const hero = r.net >= 0
    ? {
      cap: s.heroCap(year, year + 1), val: usd(r.tax), color: r.tax ? C.text : C.accent,
      sub: s.heroSub(usdSigned(r.net), stateRate ? fmt.number(stateRate) : ''),
      parts: [
        { label: s.partShort, val: usdSigned(r.shortTotal), sub: s.partShortSub(bracket), color: tone(r.shortTotal) },
        { label: s.partLong, val: usdSigned(r.longTotal), sub: s.partLongSub(r.ltRate), color: tone(r.longTotal) },
        { label: s.part6781, val: with6781 ? usdSigned(f.line7) : '—', sub: with6781 ? s.part6781Sub : s.part6781Off, color: with6781 ? tone(f.line7) : C.faint }
      ]
    }
    : {
      cap: s.heroLossCap(year, year + 1), val: usdSigned(r.net), color: C.danger,
      sub: s.heroLossSub(usd(r.lossAllowed), usd(r.lossSaving)),
      parts: [
        { label: s.partAllowed, val: '−' + usd(r.lossAllowed), sub: s.partAllowedSub(usd(r.lossLimit)), color: C.accentHi },
        { label: s.partCarryShort, val: usd(r.carryShort), sub: s.partCarrySub(year + 1), color: r.carryShort ? GOLD : C.faint },
        { label: s.partCarryLong, val: usd(r.carryLong), sub: s.partCarrySub(year + 1), color: r.carryLong ? GOLD : C.faint }
      ]
    };

  const shortRowsGain = r.shortSales.reduce((sum, row) => sum + row.gain, 0);
  const longRowsGain = r.longSales.reduce((sum, row) => sum + row.gain, 0);
  const gainOf = key => r.boxes.find(row => row.key === key).gain;

  return {
    include,
    toggleJournal: () => set('journal', include ? 'no' : 'yes'),
    missing: journal.missing,
    hero,
    left: [
      panel({ key: 'opts', title: s.rateTitle, sub: s.rateSub },
        h('div', {
          key: 'br',
          style: css({
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))', gap: '4px', padding: '3px',
            borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}`
          })
        }, ...US_BRACKETS.map(rate => h('div', {
          key: 'b' + rate, ...tap(() => set('bracket', rate), rate + '%'), class: 'pt-button', 'aria-pressed': String(rate === bracket),
          style: css({
            height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', cursor: 'pointer',
            background: rate === bracket ? C.strokeHi : 'transparent', boxShadow: rate === bracket ? `inset 0 0 0 1px ${C.accent}` : 'none',
            fontFamily: MONO, fontSize: '13px', fontWeight: 700, color: rate === bracket ? C.text : C.muted
          })
        }, rate + '%'))),
        optionRow(s.separate, s.separateSub, segmented([['no', x.de.no], ['yes', x.de.yes]], separate ? 'yes' : 'no', next => set('separate', next)), 'sep'),
        optionRow(s.with6781, s.with6781Sub, segmented([['yes', x.de.yes], ['no', x.de.no]], with6781 ? 'yes' : 'no', next => set('with6781', next)), '6781'),
        fields(box('state', x.us.state, x.us.stateHint, '%'), box('carryShort', s.carryShort), box('carryLong', s.carryLong))),
      panel({
        key: 'journal', title: s.jTitle, sub: s.jSub, res: usdSigned(journalShort), resColor: tone(journalShort), resLab: s.shortLab
      },
        include && journalNote(x.fromJournal, journalCount ? s.jText(journalCount, stockRows.map(row => row.root).slice(0, 6).join(', ')) : s.jNone),
        note(include ? s.jNote : x.journalOff)),
      boxPanel('A'),
      boxPanel('D'),
      ...(allBoxes ? ['B', 'C', 'E', 'F'].map(boxPanel) : []),
      h('div', { key: 'more' }, button(allBoxes ? s.fewerBoxes : s.moreBoxes, () => set('allBoxes', allBoxes ? 'no' : 'yes'), { tone: 'muted' })),
      panel({ key: 'sales', title: s.salesTitle, sub: s.salesSub, res: usdSigned(shortRowsGain + longRowsGain), resColor: tone(shortRowsGain + longRowsGain), resLab: x.result },
        rows.length > 0 && h('div', { key: 'list', style: css({ display: 'flex', flexDirection: 'column' }) },
          ...r.sales.map((row, i) => h('div', {
            key: 'r' + i,
            style: css({ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', alignItems: 'center', gap: '10px', padding: '7px 0', borderTop: i ? `1px solid ${C.line}` : 'none' })
          },
            h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
              h('span', { style: css({ fontSize: '13px', fontWeight: 600 }) }, row.description),
              h('span', { style: css({ fontFamily: MONO, fontSize: '11px', color: C.faint }) },
                row.acquired + ' → ' + row.sold + ' · ' + (row.long ? s.longLab : s.shortLab))),
            h('span', { style: css({ fontFamily: MONO, fontSize: '12.5px', color: tone(row.gain) }) }, usdSigned(row.gain)),
            removeButton(() => set('rows', rows.filter((_, j) => j !== i)), {}, s.removeSale)))),
        h('div', { key: 'add', style: css({ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))' }) },
          h('label', { key: 'd', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, gridColumn: '1 / -1' }) },
            h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.desc),
            input(inputs, 'u8_desc', s.descPh, { ariaLabel: s.desc, style: { height: '44px', boxSizing: 'border-box', width: '100%', borderRadius: '10px' } })),
          dateBox('u8_acq', s.acquired),
          dateBox('u8_sold', s.sold),
          fieldBox({ inputs, name: 'u8_rowProceeds', label: s.proceeds, unit: '$' }),
          fieldBox({ inputs, name: 'u8_rowCost', label: s.cost, unit: '$' })),
        h('div', { key: 'go', style: css({ display: 'flex', justifyContent: 'flex-end' }) }, button(s.addSale, addSale)),
        note(s.salesNote))
    ],
    right: [
      formCard(s.formTitle, s.formSub(year), [
        { no: s.part('I'), label: s.partI, strong: true, val: '' },
        { no: s.line('1b'), label: s.boxLine('A'), sub: s.boxLineSub.A, val: usdSigned(gainOf('A') + r.journalShort), color: tone(gainOf('A') + r.journalShort) },
        allBoxes && { no: s.line('2'), label: s.boxLine('B'), val: usdSigned(gainOf('B')), color: tone(gainOf('B')) },
        { no: s.line('3'), label: s.boxLine('C'), sub: s.boxLineSub.C, val: usdSigned(gainOf('C') + shortRowsGain), color: tone(gainOf('C') + shortRowsGain) },
        with6781 && { no: s.line('4'), label: s.from6781Short, val: usdSigned(r.f6781Short), color: tone(r.f6781Short) },
        r.carryShortIn > 0 && { no: s.line('6'), label: s.carryShort, val: '−' + usd(r.carryShortIn), color: C.danger },
        { no: s.line('7'), label: s.netShort, val: usdSigned(r.shortTotal), color: tone(r.shortTotal), strong: true },
        { no: s.part('II'), label: s.partII, strong: true, val: '' },
        { no: s.line('8b'), label: s.boxLine('D'), val: usdSigned(gainOf('D')), color: tone(gainOf('D')) },
        allBoxes && { no: s.line('9'), label: s.boxLine('E'), val: usdSigned(gainOf('E')), color: tone(gainOf('E')) },
        { no: s.line('10'), label: s.boxLine('F'), sub: s.boxLineSub.F, val: usdSigned(gainOf('F') + longRowsGain), color: tone(gainOf('F') + longRowsGain) },
        with6781 && { no: s.line('11'), label: s.from6781Long, val: usdSigned(r.f6781Long), color: tone(r.f6781Long) },
        r.carryLongIn > 0 && { no: s.line('14'), label: s.carryLong, val: '−' + usd(r.carryLongIn), color: C.danger },
        { no: s.line('15'), label: s.netLong, val: usdSigned(r.longTotal), color: tone(r.longTotal), strong: true },
        { no: s.line('16'), label: s.netAll, val: usdSigned(r.net), color: tone(r.net), strong: true },
        r.net < 0 && { no: s.line('21'), label: s.lossLine(usd(r.lossLimit)), val: '−' + usd(r.lossAllowed) },
        { label: r.net < 0 ? s.totalLoss : s.totalTax, val: r.net < 0 ? usd(r.carryShort + r.carryLong) : usd(r.tax), total: true, color: r.net < 0 ? GOLD : (r.tax ? C.text : C.accent) }
      ]),
      notesCard([
        ok(s.notes.basis),
        ok(s.notes.holding),
        warn(s.notes.wash),
        aside(s.notes.niit)
      ])
    ],
    disclaimer: s.disclaimer
  };
}

// --------------------------------------------- Germany · Anlage KAP ----

/* The journal's futures-and-CFD basket, shared by Germany and the UK. */
function futuresPanel({ s, x, include, journal, money, signed }) {
  const net = journal.gains - journal.losses;
  return panel({ key: 'fut', title: s.futTitle, sub: s.futSub, res: signed(net), resColor: tone(net), resLab: x.result },
    include && journalNote(x.fromJournal, journal.all.length
      ? x.futText(journal.all.length, money(journal.gains), money(journal.losses))
      : x.noTrades),
    !include && note(x.journalOff));
}

function deModel({ state, fmt, inputs, actions, x, year, period }) {
  const s = x.de;
  const saved = savedFor(state, 'de');
  const include = saved.journal !== 'no';
  const joint = saved.joint === 'yes';
  const church = [0.08, 0.09].includes(Number(saved.church)) ? Number(saved.church) : 0;
  const eur = wholeIn(fmt, 'EUR');
  const eurSigned = signedIn(fmt, 'EUR');
  const journal = journalIn(state, period, 'EUR', include);
  const futures = journal.gains - journal.losses;
  const r = deTax({
    futures,
    shareGains: figure(saved, 'shareGains'), shareLosses: figure(saved, 'shareLosses'), shareCarry: figure(saved, 'shareCarry'),
    dividends: figure(saved, 'dividends'), withheld: figure(saved, 'withheld'), otherCarry: figure(saved, 'otherCarry'),
    joint, church
  });
  const box = (key, label) => fieldBox({
    inputs, name: 'de_' + key, label, value: saved[key], unit: '€', onInput: event => actions.setTaxInput('de', key, event.target.value)
  });
  const pct = Math.round(church * 100);

  return {
    include,
    toggleJournal: () => actions.setTaxInput('de', 'journal', include ? 'no' : 'yes'),
    missing: journal.missing,
    hero: {
      cap: s.heroCap(year, year + 1),
      val: eur(r.tax),
      color: r.tax ? C.text : C.accent,
      sub: r.total < 0 ? s.heroLoss(eurSigned(r.total), year + 1) : s.heroSub(eur(r.allowanceUsed), eur(r.taxable)),
      parts: [
        { label: s.partCap, val: eur(r.capitalTax), sub: s.partCapSub(eur(r.taxable)) },
        { label: s.partSoli, val: eur(r.soli), sub: s.partSoliSub },
        { label: s.partChurch, val: eur(r.churchTax), sub: s.partChurchSub(pct), color: church ? C.text : C.faint }
      ]
    },
    left: [
      panel({ key: 'opts', title: s.settings },
        optionRow(s.joint, s.jointSub, segmented([['no', s.no], ['yes', s.yes]], joint ? 'yes' : 'no',
          next => actions.setTaxInput('de', 'joint', next)), 'joint'),
        optionRow(s.church, s.churchSub, segmented([[0, s.none], [0.08, '8%'], [0.09, '9%']], church,
          next => actions.setTaxInput('de', 'church', next)), 'church')),
      futuresPanel({ s, x, include, journal, money: eur, signed: eurSigned }),
      panel({
        key: 'shares', title: s.shareTitle, sub: s.shareSub, res: eurSigned(r.shareNet), resColor: tone(r.shareNet),
        resLab: r.shareNet < 0 ? s.shareLossLab : x.result
      },
        fields(box('shareGains', s.shareGains), box('shareLosses', s.shareLosses), box('shareCarry', s.shareCarry)),
        r.shareNet < 0 && note(s.shareCarryNote(eur(-r.shareNet), year + 1), GOLD)),
      panel({ key: 'div', title: s.divTitle, sub: s.divSub, res: eur(figure(saved, 'dividends')), resLab: x.gross },
        fields(box('dividends', s.dividends), box('withheld', s.withheld), box('otherCarry', s.otherCarry)))
    ],
    right: [
      formCard(s.formTitle, s.formSub, [
        { label: s.lines.total, sub: s.lines.totalSub, val: eurSigned(r.total), color: tone(r.total) },
        { label: s.lines.shareGains, sub: s.lines.shareGainsSub, val: eur(figure(saved, 'shareGains')) },
        { label: s.lines.shareLosses, sub: s.lines.shareLossesSub, val: eur(figure(saved, 'shareLosses')) },
        { label: s.lines.fut, sub: s.lines.futSub, val: eurSigned(futures), color: tone(futures) },
        { label: s.lines.allowance, sub: s.lines.allowanceSub(eur(r.allowance)), val: '−' + eur(r.allowanceUsed) },
        { label: s.lines.cap, sub: s.lines.capSub(church, r.credit ? eur(r.credit) : ''), val: eur(r.capitalTax) },
        { label: s.lines.soli, val: eur(r.soli) },
        { label: s.lines.church, val: eur(r.churchTax) },
        { label: s.lines.total2, val: eur(r.tax), total: true, color: r.tax ? C.text : C.accent }
      ]),
      notesCard([ok(s.notes.german), warn(s.notes.foreign), aside(s.notes.guenstiger)])
    ],
    disclaimer: s.disclaimer
  };
}

// ------------------------------------------------------ the UK · SA108 ----

function ukModel({ state, fmt, inputs, actions, x, year, period }) {
  const s = x.uk;
  const saved = savedFor(state, 'uk');
  const include = saved.journal !== 'no';
  const rate = Number(saved.rate) === 24 ? 24 : 18;
  const gbp = wholeIn(fmt, 'GBP');
  const gbpSigned = signedIn(fmt, 'GBP');
  const journal = journalIn(state, period, 'GBP', include);
  const r = ukTax({
    gains: journal.gains, losses: journal.losses,
    proceeds: figure(saved, 'proceeds'), cost: figure(saved, 'cost'), broughtForward: figure(saved, 'bf'), rate
  });
  const box = (key, label) => fieldBox({
    inputs, name: 'uk_' + key, label, value: saved[key], unit: '£', onInput: event => actions.setTaxInput('uk', key, event.target.value)
  });

  return {
    include,
    toggleJournal: () => actions.setTaxInput('uk', 'journal', include ? 'no' : 'yes'),
    missing: journal.missing,
    hero: {
      cap: s.heroCap(period.label, year + 2),
      val: gbp(r.tax),
      color: r.tax ? C.text : C.accent,
      sub: r.net < 0 ? s.heroLoss(gbpSigned(r.net)) : s.heroSub(gbpSigned(r.net)),
      parts: [
        { label: s.partNet, val: gbpSigned(r.net), sub: s.partNetSub(gbp(r.gains)), color: tone(r.net) },
        { label: s.partAea, val: gbp(UK_AEA), sub: s.partAeaSub },
        { label: s.partCf, val: gbp(r.carriedForward), sub: s.partCfSub, color: r.carriedForward ? GOLD : C.faint }
      ]
    },
    left: [
      panel({ key: 'opts', title: s.settings },
        optionRow(s.band, s.bandSub, segmented([[18, s.basic], [24, s.higher]], rate, next => actions.setTaxInput('uk', 'rate', next)), 'band')),
      futuresPanel({ s, x, include, journal, money: gbp, signed: gbpSigned }),
      panel({ key: 'shares', title: s.shareTitle, sub: s.shareSub, res: gbpSigned(r.shareNet), resColor: tone(r.shareNet), resLab: x.result },
        fields(box('proceeds', s.proceeds), box('cost', s.cost)),
        note(s.shareNote)),
      panel({ key: 'bf', title: s.bfTitle, sub: s.bfSub, res: gbp(r.bfUsed), resColor: r.bfUsed ? C.accent : C.textDim, resLab: s.bfLab },
        fields(box('bf', s.bf)),
        note(s.bfNote))
    ],
    right: [
      formCard(s.formTitle, s.formSub(period.label), [
        { label: s.lines.gains, val: gbp(r.gains) },
        { label: s.lines.losses, val: gbp(r.losses) },
        { label: s.lines.net, val: gbpSigned(r.net), color: tone(r.net), strong: true },
        { label: s.lines.bf, val: '−' + gbp(r.bfUsed) },
        { label: s.lines.aea, sub: s.lines.aeaSub, val: '−' + gbp(r.exempt) },
        { label: s.lines.taxable, val: gbp(r.taxable) },
        { label: s.lines.tax(r.rate), val: gbp(r.tax), total: true, color: r.tax ? C.text : C.accent },
        { label: s.lines.cf, val: gbp(r.carriedForward) }
      ]),
      notesCard([
        r.mustReport ? warn(s.notes.must) : ok(s.notes.mustNot),
        ok(s.notes.year),
        aside(s.notes.spread)
      ])
    ],
    disclaimer: s.disclaimer
  };
}
